import { useMemo, useState } from 'react';
import { applyFilterFn, TItem } from '@utils/filter';
import { TCatalogFilter } from 'types/filter';
import { TSessionsData } from '@routes/catalog/hooks/useSessionsData';
import { useFilterConfig } from '@routes/catalog/hooks/useFilterConfig';
import { TFilterConfig } from '@fe-learner-common';
import { sort, SortOrder } from '@utils/sortSessions';

export const initialFilter = (): TCatalogFilter => ({
  search: '',
  date: {
    start: '',
    end: '',
  },
  duration: {
    min: 0,
    max: 0,
  },
  award: new Set<string>(),
  format: new Set<string>(),
  language: new Set<string>(),
  location: new Set<string>(),
});

type TFilterValue = TCatalogFilter[keyof TCatalogFilter];

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isFilterValueEqual = (a: TFilterValue, b: TFilterValue): boolean => {
  if (a instanceof Set && b instanceof Set) {
    return a.size === b.size && Array.from(a).every((value) => b.has(value));
  }

  if (isObject(a) && isObject(b)) {
    return Object.keys(a).every((key: keyof TFilterValue) =>
      isFilterValueEqual(a[key], b[key]),
    );
  }

  return a === b;
};

export const serializeActiveFilters = (
  filter: TCatalogFilter,
): Record<string, unknown> => {
  const defaultFilter = initialFilter();
  const appliedFilter: Record<string, unknown> = {};

  for (const key in filter) {
    const typedKey = key as keyof TCatalogFilter;
    if (!isFilterValueEqual(filter[typedKey], defaultFilter[typedKey])) {
      appliedFilter[key] =
        filter[typedKey] instanceof Set
          ? Array.from(filter[typedKey])
          : filter[typedKey];
    }
  }

  return appliedFilter;
};

export const useCatalogFilter = ({
  sessionData,
  category,
  sortOrder,
}: {
  sessionData?: TSessionsData | null;
  category?: number | null;
  sortOrder: SortOrder;
}) => {
  const { items, sessionMap } = sessionData || {
    items: undefined,
    sessionMap: undefined,
  };
  const [filter, setFilter] = useState<TCatalogFilter>(initialFilter());

  const filterConfig: TFilterConfig | null = useFilterConfig({
    items,
    filter,
  });

  const sortFn = sort(sortOrder);
  const filterFn = applyFilterFn({
    filter,
    category,
    sessionMap,
  });

  const filteredItems: TItem[] | undefined = useMemo(() => {
    return items?.filter(filterFn).sort(sortFn);
  }, [items, filterFn, sortFn]);

  return {
    filter,
    filterConfig,
    setFilter,
    filteredItems,
    clearFilter: () => setFilter(initialFilter()),
  };
};
